import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import DaumPostcodeEmbed from "react-daum-postcode";
import { toast, Toaster } from "react-hot-toast";
import { useMorningAuthStore } from "../../../../../store/useMorningAuthStore";
import Input from "../../-components/common/Input";
import Button from "../../-components/common/Button";
import AddressBook from "../../-components/AddressBook";
import { AddressItem, usePostAddressBook } from "hooks/morning/usePostAddressBook";
import { ReactComponent as Close } from "assets/morning/icon/close.svg";
import { ReactComponent as ArrowBack } from "assets/common/common/arrowBack.svg";
import { X } from "lucide-react";

const RequestQuickPickup = () => {
    const navigate = useNavigate();
    const c_code = useMorningAuthStore(state => state.c_code ?? "");
    const isLoggedInIlshin = c_code === "0500001";
    const [showAddressBook, setShowAddressBook] = useState(false);
    const [showArrivePostcode, setShowArrivePostcode] = useState(false);
    const [arriveIsFocused, setArriveIsFocused] = useState(false);
    const [errors, setErrors] = useState<{ [key: string]: boolean }>({});
    const [arriveFocusedIndex, setArriveFocusedIndex] = useState<number>(-1);
    const arriveItemRefs = useRef<(HTMLDivElement | null)[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessages, setErrorMessages] = useState<Record<string, string>>({});

    // 필드 refs
    const senderNameRef = useRef<HTMLDivElement>(null);
    const senderTelRef = useRef<HTMLDivElement>(null);
    const arriveNameRef = useRef<HTMLDivElement>(null);
    const arriveTelRef = useRef<HTMLDivElement>(null);
    const arrivePostcodeRef = useRef<HTMLDivElement>(null);
    const arriveAddressRef = useRef<HTMLDivElement>(null);
    // 접수자 정보
    const [sender, setSender] = useState({
        name: "",
        tel: "",
    });
    // 도착지 정보
    const [arrive, setArrive] = useState({
        name: "",
        tel: "",
        manager: "",
        mobile: "",
        postcode: "",
        address: "",
        addressDetail: "",
    });
    // 도착지 주소록 검색
    const [arriveSearchData, setArriveSearchData] = useState<AddressItem[]>([]);
    const { mutate: arriveAddressBookSearchMutate } = usePostAddressBook((data: AddressItem[]) => {
        setArriveSearchData(data);
    });

    const formatMobileNumber = (value: string) => {
        const cleaned = value.replace(/\D/g, "").slice(0, 11);
        if (cleaned.length < 4) return cleaned;
        if (cleaned.length < 8) return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
        return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 7)}-${cleaned.slice(7)}`;
    };

    // 도착지 주소 검색
    const handleArriveAddress = (data: any) => {
        let fullAddress = data.roadAddress || data.jibunAddress;

        const extras: string[] = [];

        if (data.bname) extras.push(data.bname);

        if (extras.length > 0) {
            fullAddress += ` (${extras.join(", ")})`;
        }

        setArrive((prev: any) => ({
            ...prev,
            postcode: data.zonecode,
            address: fullAddress,
        }));

        setErrors(prev => ({
            ...prev,
            arrivePostcode: false,
            arriveAddress: false,
        }));

        setShowArrivePostcode(false);
    };
    // 도착지 주소록 선택
    const handleAddressSelect = (item: any) => {
        // console.log(item);
        setArrive({
            name: item.a_company || "",
            tel: item.a_tel || "",
            manager: item.a_name || "",
            postcode: item.a_post || "",
            address: item.a_address || "",
            addressDetail: item.a_address_detail || "",
            // t_c_code: item.a_code || "",
            mobile: item.a_mobile || "",
        });

        setErrors(prev => ({
            ...prev,
            arriveName: false,
            arriveTel: false,
            arrivePostcode: false,
            arriveAddress: false,
            ...(item.a_name && { arriveManager: false }),
        }));

        setShowAddressBook(false);
    };

    const isHandlingArriveKey = useRef(false);

    const handleArriveKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (isHandlingArriveKey.current) return;
        isHandlingArriveKey.current = true;
        setTimeout(() => {
            isHandlingArriveKey.current = false;
        }, 50);

        if (!arriveSearchData || arriveSearchData.length === 0) return;

        if (e.key === "ArrowDown") {
            e.preventDefault();
            e.stopPropagation();
            setArriveFocusedIndex(prev => {
                if (prev === -1) return 0;
                if (prev < arriveSearchData.length - 1) return prev + 1;
                return 0;
            });
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            e.stopPropagation();
            setArriveFocusedIndex(prev => {
                if (prev > 0) return prev - 1;
                return arriveSearchData.length - 1;
            });
        } else if (e.key === "Enter") {
            e.preventDefault();
            e.stopPropagation();
            if (arriveFocusedIndex >= 0 && arriveFocusedIndex < arriveSearchData.length) {
                const selected = arriveSearchData[arriveFocusedIndex];
                // console.log("arrive selected", selected);
                setArrive((prev: any) => ({
                    ...prev,
                    name: selected.a_company || "",
                    tel: selected.a_tel || "",
                    postcode: selected.a_post || "",
                    address: selected.a_address || "",
                    addressDetail: selected.a_address_detail || "",
                    t_c_code: selected.a_code || "",
                }));
                setArriveIsFocused(false);
                setArriveFocusedIndex(-1);
            }
        } else if (e.key === "Escape") {
            e.preventDefault();
            e.stopPropagation();
            setArriveIsFocused(false);
            setArriveFocusedIndex(-1);
        }
    };

    const validateFields = () => {
        const errors: Record<string, string> = {};

        // 접수자 정보 검증
        if (!sender.name || sender.name.trim() === "") {
            errors.senderName = "접수자 이름을 입력하세요.";
        }
        if (!sender.tel || sender.tel.trim() === "") {
            errors.senderTel = "접수자 전화번호를 입력하세요.";
        }

        // 도착지 정보 검증
        if (!arrive.name || arrive.name.trim() === "") {
            errors.arriveName = "상호명을 입력하세요.";
        }
        if (!arrive.tel || arrive.tel.trim() === "") {
            errors.arriveTel = "연락처를 입력하세요.";
        }
        if (!arrive.postcode || arrive.postcode.trim() === "") {
            errors.arrivePostcode = "도착지를 입력하세요.";
        }
        if (!arrive.address || arrive.address.trim() === "") {
            errors.arriveAddress = "도착지 주소를 입력하세요.";
        }

        return errors;
    };

    // 에러 발생한 첫 번째 필드로 스크롤
    const scrollToFirstError = (errorKeys: string[]) => {
        // 에러 필드 우선순위에 따라 ref 매핑
        const errorRefMap: Record<string, React.RefObject<HTMLDivElement>> = {
            senderName: senderNameRef,
            senderTel: senderTelRef,
            arriveName: arriveNameRef,
            arriveTel: arriveTelRef,
            arrivePostcode: arrivePostcodeRef,
            arriveAddress: arriveAddressRef,
        };

        // 첫 번째 에러 필드 찾기
        for (const key of errorKeys) {
            const ref = errorRefMap[key];
            if (ref?.current) {
                setTimeout(() => {
                    ref.current?.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                    });
                }, 100);
                break;
            }
        }
    };

    const handleSubmit = () => {
        setIsSubmitting(true);
        const validationErrors = validateFields();
        const errorKeys = Object.keys(validationErrors);
        if (errorKeys.length > 0) {
            setErrorMessages(validationErrors);
            // errors 상태도 업데이트 (boolean 값으로)
            const errorFlags: { [key: string]: boolean } = {};
            errorKeys.forEach(key => {
                errorFlags[key] = true;
            });
            setErrors(errorFlags);
            // 첫 번째 에러 필드로 스크롤
            scrollToFirstError(errorKeys);
            setIsSubmitting(false);
            return;
        }

        // 검증 통과 시 처리
        // const params = {
        //     getPage: page,
        //     getPageSize: 11,
        //     bl_no: blNo,
        //     o_kind: status === 0 ? "" : status.toString(),
        //     s_a_name: sender,
        //     s_b_contact: senderTel,
        //     s_date: startDate ? startDate.toISOString().split("T")[0] : "",
        //     e_date: endDate ? endDate.toISOString().split("T")[0] : "",
        //     s_kind: send.toString(),
        //     s_p_dept: department === "all" ? "" : department,
        // };
        // navigate("/customer/tracking", { state: { filter: params } });
        // console.log("sender", sender);
        // console.log("arrive", arrive);

        setIsSubmitting(false);
        toast.success("접수가 완료되었습니다.");
        // navigate("/customer/tracking");
    };

    useEffect(() => {
        if (arriveFocusedIndex >= 0 && arriveItemRefs.current[arriveFocusedIndex]) {
            arriveItemRefs.current[arriveFocusedIndex]?.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
            });
        }
    }, [arriveFocusedIndex]);

    // 뒤로가기
    useEffect(() => {
        if (!isLoggedInIlshin) {
            navigate(-1);
        }
    }, [isLoggedInIlshin, navigate]);

    return (
        <div className="fixed inset-0 z-[500] bg-white flex flex-col  overflow-hidden">
            {/* <div className="flex gap-2 min-h-[60px] items-center px-[4%] relative">
                <button type="button" className="flex" onClick={() => navigate(-1)}>
                    <ArrowBack />
                    <span className="indent-[-9999em] absolute">닫기</span>
                </button>

                <p className="text-[20px] font-[500] absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2">퀵 픽업요청</p>
            </div> */}
            <div className="flex-1 overflow-auto pb-[150px] px-[4%] flex flex-col pb-[10px]">
                <div className="py-3">
                    <div className="border-b-[2px] justify-between border-black flex items-center pb-2 flex-wrap gap-y-2">
                        <div className="flex gap-4">
                            <p className="text-[28px] font-bold responsive1200:text-[20px] text-nowrap">접수 정보</p>
                        </div>
                    </div>
                    <div>
                        <div className="py-3 flex flex-col">
                            <div className="text-[18px] font-[500] mb-[8px]">
                                접수자 이름 <span className="text-new text-[16px] font-normal">*</span>
                            </div>
                            <Input
                                className="w-full"
                                value={sender.name}
                                onChange={e => {
                                    setSender((prev: any) => ({ ...prev, name: e.target.value }));
                                    if (isSubmitting) {
                                        const isInvalid = e.target.value.trim().length === 0;
                                        setErrors(prev => ({ ...prev, senderName: isInvalid }));
                                    } else {
                                        setErrors(prev => ({ ...prev, senderName: false }));
                                    }
                                }}
                                placeholder="접수자 이름을 입력해주세요."
                                error={errors?.senderName}
                            />
                            {errors?.senderName && <p className="text-[15px] text-[#ff4242] mt-1">{errorMessages?.senderName}</p>}
                        </div>
                        <div ref={senderTelRef} className="py-3 flex flex-col">
                            <div className="text-[18px] font-[500] mb-[8px]">
                                접수자 전화번호 <span className="text-new text-[16px] font-normal">*</span>
                            </div>
                            <Input
                                placeholder="접수자 전화번호를 입력해주세요."
                                className="w-full"
                                value={sender.tel}
                                onChange={e => {
                                    setSender((prev: any) => ({ ...prev, tel: e.target.value }));
                                    if (isSubmitting) {
                                        const isInvalid = e.target.value.trim().length === 0;
                                        setErrors(prev => ({ ...prev, senderTel: isInvalid }));
                                    } else {
                                        setErrors(prev => ({ ...prev, senderTel: false }));
                                    }
                                }}
                                error={errors?.senderTel}
                            />
                            {errors?.senderTel && <p className="text-[15px] text-[#ff4242] mt-1">{errorMessages?.senderTel}</p>}
                        </div>
                    </div>
                </div>

                <div className="py-3">
                    <div className="border-b-[2px] justify-between border-black flex items-center pb-2 flex-wrap gap-y-2">
                        <div className="flex gap-4">
                            <p className="text-[28px] font-bold responsive1200:text-[20px] text-nowrap">도착지 정보</p>
                            <Button
                                onClick={() => {
                                    setArrive((prev: any) => ({
                                        ...prev,
                                        name: "",
                                        tel: "",
                                        manager: "",
                                        mobile: "",
                                        postcode: "",
                                        address: "",
                                        addressDetail: "",
                                    }));
                                    setShowArrivePostcode(false);
                                }}
                            >
                                주소리셋
                            </Button>
                        </div>
                        <div className="flex gap-1.5">
                            {c_code && (
                                <Button
                                    onClick={() => {
                                        setShowAddressBook(true);
                                        // addressBookTarget.current = "arrive";
                                    }}
                                    color="new"
                                >
                                    주소록
                                </Button>
                            )}

                            {/* {<Button onClick={handleSameToArrive}>접수자 정보 동일</Button>} */}
                        </div>
                    </div>
                    <div>
                        <div ref={arriveNameRef} className={`flex relative flex-col py-3 gap-2`}>
                            <div className={`text-[18px] font-[500] min-w-[160px]`}>
                                상호명 <span className="text-new text-[16px] font-normal">*</span>
                            </div>
                            <div className="relative w-full">
                                <div className={`flex flex-col w-full`}>
                                    <Input
                                        placeholder="상호명을 직접 입력하거나 조회하세요."
                                        value={arrive.name}
                                        onChange={e => {
                                            const value = e.target.value;

                                            setArrive((prev: any) => ({ ...prev, name: value }));
                                            arriveAddressBookSearchMutate({ searchText: value });

                                            if (isSubmitting) {
                                                let isInValidValue: boolean;
                                                if (value.trim().length === 0) {
                                                    isInValidValue = true;
                                                } else {
                                                    isInValidValue = false;
                                                }
                                                setErrors(prev => ({ ...prev, arriveName: isInValidValue }));
                                            } else {
                                                setErrors(prev => ({ ...prev, arriveName: false }));
                                            }
                                        }}
                                        error={errors?.arriveName}
                                        onFocus={() => setArriveIsFocused(true)}
                                        onKeyDown={handleArriveKeyDown}
                                        className="w-full"
                                    />
                                    {errors?.arriveName && <p className="text-[15px] text-[#ff4242] mt-1">{errorMessages?.arriveName}</p>}
                                </div>
                                {arriveIsFocused && arrive.name.length >= 1 && arriveSearchData && arriveSearchData.length > 0 && (
                                    <div className={`absolute top-[56px] left-0 z-10 border bg-white rounded-lg shadow w-full`}>
                                        <button
                                            onClick={() => {
                                                setArriveIsFocused(false);
                                                setArriveFocusedIndex(-1);
                                            }}
                                            className="absolute right-0 top-0 w-6 h-6 hover:bg-black/10 flex justify-center items-center rounded-full"
                                        >
                                            <X className="h-4 w-4" />
                                        </button>
                                        <div className={`max-h-[400px] overflow-y-auto w-full`}>
                                            {arriveSearchData.map((company, index) => {
                                                const displayInfo = `${(company as AddressItem).a_company} | ${(company as AddressItem).a_tel} | ${(company as AddressItem).a_address}`;

                                                return (
                                                    <div
                                                        key={`${company.a_code}-${index}`}
                                                        ref={el => (arriveItemRefs.current[index] = el)}
                                                        className={`px-4 py-2 hover:bg-gray-100 cursor-pointer text-[16px] ${
                                                            arriveFocusedIndex === index ? "bg-gray-200" : "hover:bg-gray-100"
                                                        }`}
                                                        onClick={() => {
                                                            setArrive((prev: any) => ({
                                                                ...prev,
                                                                name: company.a_company,
                                                                tel: company.a_tel,
                                                                postcode: company.a_post,
                                                                address: company.a_address,
                                                                addressDetail: company.a_address_detail,
                                                                t_c_code: company.a_code,
                                                            }));
                                                            setErrors(prev => ({
                                                                ...prev,
                                                                arriveName: false,
                                                                arriveTel: false,
                                                                arrivePostcode: false,
                                                                arriveAddress: false,
                                                            }));
                                                            setArriveIsFocused(false);
                                                        }}
                                                    >
                                                        {displayInfo}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div ref={arriveTelRef} className="flex flex-col gap-[8px] py-3">
                            <div className={`text-[18px] font-[500] min-w-[160px]`}>
                                연락처 <span className="text-new text-[16px] font-normal">*</span>
                            </div>
                            <div className={`flex flex-col w-full`}>
                                <Input
                                    placeholder="연락처를 입력해주세요."
                                    className="w-full"
                                    value={arrive.tel}
                                    onChange={e => {
                                        const rawValue = e.target.value;
                                        const limitedValue = rawValue.slice(0, 13);

                                        setArrive((prev: any) => ({ ...prev, tel: limitedValue }));

                                        if (isSubmitting) {
                                            const isInvalid = limitedValue.trim().length === 0;
                                            setErrors(prev => ({ ...prev, arriveTel: isInvalid }));
                                        } else {
                                            setErrors(prev => ({ ...prev, arriveTel: false }));
                                        }
                                    }}
                                    error={errors?.arriveTel}
                                />
                                {errors?.arriveTel && <p className="text-[15px] text-[#ff4242] mt-1">{errorMessages?.arriveTel}</p>}
                            </div>
                        </div>
                        <div className="flex flex-col gap-[8px] py-3">
                            <div className="text-[18px] font-[500] min-w-[160px]">담당자명</div>
                            <div className={`flex flex-col gap-2 w-full`}>
                                <Input
                                    placeholder="담당자명을 입력해주세요."
                                    className="w-full"
                                    value={arrive.manager}
                                    onChange={e => {
                                        setArrive((prev: any) => ({ ...prev, manager: e.target.value }));
                                    }}
                                />
                            </div>
                        </div>
                        <div className="flex flex-col gap-[8px] py-3">
                            <div className="text-[18px] font-[500] min-w-[160px]">휴대폰 번호</div>
                            <div className="responsive1200:w-full">
                                <Input
                                    placeholder="휴대폰 번호를 입력해주세요."
                                    className="w-full"
                                    value={arrive.mobile}
                                    onChange={e => {
                                        const formatted = formatMobileNumber(e.target.value);
                                        setArrive((prev: any) => ({ ...prev, mobile: formatted }));
                                    }}
                                />
                                {/* <p className="text-new text-[14px] mt-2 ml-1 break-keep">
                                    휴대폰번호 입력시 수신자분께 배송 알림을 발송해드립니다.
                                </p> */}
                            </div>
                        </div>
                        <div className="flex flex-col gap-[8px] py-3">
                            <div className="text-[18px] font-[500] min-w-[160px] mb-1 responsive1200:mb-[8px]">
                                도착지 주소 <span className="text-new text-[16px] font-normal">*</span>
                            </div>
                            <div ref={arrivePostcodeRef}>
                                <div className="w-full flex gap-1.5">
                                    <Input
                                        disabled
                                        onClick={() => setShowArrivePostcode(true)}
                                        className="w-full"
                                        value={arrive.postcode}
                                        onChange={e => {
                                            setArrive((prev: any) => ({
                                                ...prev,
                                                postcode: e.target.value,
                                            }));
                                            if (isSubmitting) {
                                                const isInvalid = e.target.value.trim().length === 0;
                                                setErrors(prev => ({ ...prev, arrivePostcode: isInvalid }));
                                            } else {
                                                setErrors(prev => ({ ...prev, arrivePostcode: false }));
                                            }
                                        }}
                                        error={errors?.arrivePostcode}
                                    />
                                    <button
                                        onClick={() => setShowArrivePostcode(true)}
                                        className="min-w-[123px] border rounded-lg border-[#1B1C1E] text-[18px] font-[500]"
                                    >
                                        우편번호
                                    </button>
                                </div>
                                {errors?.arrivePostcode && (
                                    <p className="text-[15px] text-[#ff4242] mt-1">{errorMessages?.arrivePostcode}</p>
                                )}
                            </div>
                            <div ref={arriveAddressRef} className="relative">
                                <Input
                                    disabled
                                    onClick={() => setShowArrivePostcode(true)}
                                    className="w-full"
                                    value={arrive.address}
                                    onChange={e => {
                                        setArrive((prev: any) => ({
                                            ...prev,
                                            address: e.target.value,
                                        }));
                                        if (isSubmitting) {
                                            const isInvalid = e.target.value.trim().length === 0;
                                            setErrors(prev => ({ ...prev, arriveAddress: isInvalid }));
                                        } else {
                                            setErrors(prev => ({ ...prev, arriveAddress: false }));
                                        }
                                    }}
                                    error={errors?.arriveAddress}
                                />
                                {errors?.arriveAddress && <p className="text-[15px] text-[#ff4242] mt-1">{errorMessages?.arriveAddress}</p>}
                                <Input
                                    className="w-full mt-2"
                                    placeholder="상세주소 입력"
                                    value={arrive.addressDetail}
                                    onChange={e => {
                                        setArrive((prev: any) => ({ ...prev, addressDetail: e.target.value }));
                                    }}
                                />
                                {showArrivePostcode && (
                                    <div className="absolute z-[100] mt-[34px] top-[57px] bg-white border w-full rounded-lg shadow-lg overflow-hidden">
                                        <div className="px-6 py-5 text-2xl font-bold border-b border-gray3 flex items-center justify-between">
                                            <p>우편번호 찾기</p>
                                            <Close onClick={() => setShowArrivePostcode(false)} className="cursor-pointer" />
                                        </div>
                                        <DaumPostcodeEmbed className="min-h-[470px]" onComplete={handleArriveAddress} />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {showAddressBook &&
                    createPortal(
                        <div className="fixed inset-0 z-[9999] bg-black bg-opacity-40 flex items-center justify-center">
                            <AddressBook onSelect={item => handleAddressSelect(item)} onClose={() => setShowAddressBook(false)} />
                        </div>,
                        document.body,
                    )}
                <div>
                    <button type="button" className="text-white bg-new text-center py-[16px] w-full text-[20px]" onClick={handleSubmit}>
                        접수
                    </button>
                </div>
            </div>
            <Toaster position="bottom-center" reverseOrder={false} />
        </div>
    );
};

export default RequestQuickPickup;
